import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Lock, Package, DollarSign, AlertCircle, CheckCircle, XCircle, Clock, RefreshCw, Search, ShieldCheck, Eye, EyeOff, LogIn } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simple admin password - change this to your own
const ADMIN_PASSWORD = 'AxonAdmin2026!';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [stats, setStats] = useState({ total: 0, paid: 0, declined: 0, failed: 0, revenue: 0 });

  useEffect(() => {
    document.title = 'Admin Dashboard | Axon Muscle';
    // Check if already authenticated in session
    const auth = sessionStorage.getItem('axon_admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('axon_admin_auth', 'true');
      setAuthError('');
    } else {
      setAuthError('Invalid password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('axon_admin_auth');
    setPassword('');
  };

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/orders`);
      const data = await response.json();
      const orderList = data.orders || [];
      setOrders(orderList);
      
      // Calculate stats
      const paid = orderList.filter(o => o.status === 'paid').length;
      const declined = orderList.filter(o => o.status === 'declined').length;
      const failed = orderList.filter(o => o.status === 'failed').length;
      const revenue = orderList
        .filter(o => o.status === 'paid')
        .reduce((sum, o) => sum + (o.amount || 0), 0);
      
      setStats({
        total: orderList.length,
        paid,
        declined,
        failed,
        revenue
      });
    } catch (err) {
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const searchByEmail = async () => {
    if (!searchEmail.trim()) {
      fetchOrders();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/orders/email/${encodeURIComponent(searchEmail)}`);
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (err) {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-400 border border-green-500/30">
            <CheckCircle className="w-3.5 h-3.5" />
            PAID
          </span>
        );
      case 'declined':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30">
            <XCircle className="w-3.5 h-3.5" />
            DECLINED
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
            <Clock className="w-3.5 h-3.5" />
            PENDING
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-neutral-500/20 text-neutral-400 border border-neutral-500/30">
            <AlertCircle className="w-3.5 h-3.5" />
            {(status || 'UNKNOWN').toUpperCase()}
          </span>
        );
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <Header />
        <main className="pt-[140px] pb-20">
          <div className="max-w-md mx-auto px-4">
            <div className="bg-neutral-900 p-8 rounded-2xl border border-neutral-800">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-axon-600 to-axon-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
                <h1 className="font-display text-2xl text-white uppercase tracking-tight">Admin Access</h1>
                <p className="text-neutral-400 text-sm mt-2">Enter password to access the dashboard</p>
              </div>

              {authError && (
                <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-red-400 text-sm">{authError}</span>
                </div>
              )}

              <form onSubmit={handleLogin}>
                <div className="mb-6">
                  <Label className="text-sm font-semibold text-white">Password</Label>
                  <div className="relative mt-2">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="rounded-xl border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500 focus:ring-axon-500 focus:border-axon-500 pr-12"
                      placeholder="Enter admin password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  <LogIn className="w-5 h-5" />
                  Access Dashboard
                </button>
              </form>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      
      <main className="pt-[140px] pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-axon-600 to-axon-500 rounded-xl flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-display text-3xl text-white uppercase tracking-tight">Admin Dashboard</h1>
                <p className="text-neutral-500 text-sm">Order Management & Transaction Tracking</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchOrders}
                className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-white rounded-xl transition-all flex items-center gap-2 text-sm font-medium border border-neutral-700"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl transition-all text-sm font-medium border border-red-500/30"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-neutral-900 p-5 rounded-2xl border border-neutral-800">
              <p className="text-neutral-500 text-xs uppercase font-semibold mb-1">Total Orders</p>
              <p className="font-display text-3xl text-white">{stats.total}</p>
            </div>
            <div className="bg-neutral-900 p-5 rounded-2xl border border-green-500/20">
              <p className="text-green-400 text-xs uppercase font-semibold mb-1">Paid</p>
              <p className="font-display text-3xl text-green-400">{stats.paid}</p>
            </div>
            <div className="bg-neutral-900 p-5 rounded-2xl border border-red-500/20">
              <p className="text-red-400 text-xs uppercase font-semibold mb-1">Declined</p>
              <p className="font-display text-3xl text-red-400">{stats.declined}</p>
            </div>
            <div className="bg-neutral-900 p-5 rounded-2xl border border-neutral-800">
              <p className="text-neutral-500 text-xs uppercase font-semibold mb-1">Failed</p>
              <p className="font-display text-3xl text-neutral-400">{stats.failed}</p>
            </div>
            <div className="bg-neutral-900 p-5 rounded-2xl border border-axon-500/20">
              <p className="text-axon-400 text-xs uppercase font-semibold mb-1">Revenue</p>
              <p className="font-display text-3xl text-axon-400">${stats.revenue.toFixed(2)}</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-neutral-900 p-4 rounded-2xl border border-neutral-800 mb-6">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <Input
                  type="email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchByEmail()}
                  className="pl-10 rounded-xl border-neutral-700 bg-neutral-800 text-white placeholder-neutral-500 focus:ring-axon-500 focus:border-axon-500"
                  placeholder="Search by customer email..."
                />
              </div>
              <button
                onClick={searchByEmail}
                className="px-6 py-2 bg-axon-600 hover:bg-axon-500 text-white rounded-xl transition-all font-semibold text-sm"
              >
                Search
              </button>
              {searchEmail && (
                <button
                  onClick={() => { setSearchEmail(''); fetchOrders(); }}
                  className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-400 rounded-xl transition-all text-sm border border-neutral-700"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-red-400">{error}</span>
            </div>
          )}

          {/* Orders Table */}
          <div className="bg-neutral-900 rounded-2xl border border-neutral-800 overflow-hidden">
            <div className="p-5 border-b border-neutral-800">
              <h2 className="font-display text-lg text-white uppercase tracking-wide flex items-center gap-2">
                <Package className="w-5 h-5 text-axon-500" />
                Orders ({orders.length})
              </h2>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <RefreshCw className="w-8 h-8 text-axon-500 animate-spin mx-auto mb-3" />
                <p className="text-neutral-400">Loading orders...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="p-12 text-center">
                <Package className="w-10 h-10 text-neutral-600 mx-auto mb-3" />
                <p className="text-neutral-400">No orders found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-800">
                      <th className="text-left p-4 text-xs uppercase font-semibold text-neutral-500">Order ID</th>
                      <th className="text-left p-4 text-xs uppercase font-semibold text-neutral-500">Date</th>
                      <th className="text-left p-4 text-xs uppercase font-semibold text-neutral-500">Customer</th>
                      <th className="text-left p-4 text-xs uppercase font-semibold text-neutral-500">Email</th>
                      <th className="text-right p-4 text-xs uppercase font-semibold text-neutral-500">Amount</th>
                      <th className="text-left p-4 text-xs uppercase font-semibold text-neutral-500">Status</th>
                      <th className="text-left p-4 text-xs uppercase font-semibold text-neutral-500">Transaction ID</th>
                      <th className="text-center p-4 text-xs uppercase font-semibold text-neutral-500">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <React.Fragment key={order.id}>
                        <tr className="border-b border-neutral-800/50 hover:bg-neutral-800/30 transition-colors">
                          <td className="p-4">
                            <span className="text-axon-500 font-mono text-sm font-medium">
                              {order.id ? order.id.substring(0, 8).toUpperCase() : '—'}
                            </span>
                          </td>
                          <td className="p-4 text-neutral-400 text-sm">
                            {order.created_at ? new Date(order.created_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            }) : '—'}
                          </td>
                          <td className="p-4 text-white text-sm font-medium">
                            {order.customer_name || '—'}
                          </td>
                          <td className="p-4 text-neutral-400 text-sm">
                            {order.customer_email || '—'}
                          </td>
                          <td className="p-4 text-right">
                            <span className="text-white font-semibold">${(order.amount || 0).toFixed(2)}</span>
                          </td>
                          <td className="p-4">
                            {getStatusBadge(order.status)}
                          </td>
                          <td className="p-4 text-neutral-400 font-mono text-sm">
                            {order.transaction_id || '—'}
                          </td>
                          <td className="p-4 text-center">
                            <button
                              onClick={() => setSelectedOrder(selectedOrder === order.id ? null : order.id)}
                              className="p-2 hover:bg-neutral-700 rounded-lg transition-colors text-neutral-400 hover:text-white"
                            >
                              {selectedOrder === order.id ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </td>
                        </tr>
                        {selectedOrder === order.id && (
                          <tr className="border-b border-neutral-800/50">
                            <td colSpan="8" className="p-4 bg-neutral-800/30">
                              <div className="grid md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                  <p className="text-xs uppercase font-semibold text-neutral-500">Full Order ID</p>
                                  <p className="text-white font-mono text-xs break-all">{order.id}</p>
                                </div>
                                <div className="space-y-2">
                                  <p className="text-xs uppercase font-semibold text-neutral-500">Auth Code</p>
                                  <p className="text-white font-mono">{order.auth_code || '—'}</p>
                                </div>
                                <div className="space-y-2">
                                  <p className="text-xs uppercase font-semibold text-neutral-500">Response</p>
                                  <p className="text-white text-sm">{order.response_text || '—'}</p>
                                </div>
                                <div className="space-y-2">
                                  <p className="text-xs uppercase font-semibold text-neutral-500">AVS Response</p>
                                  <p className="text-white font-mono">{order.avs_response || '—'}</p>
                                </div>
                                <div className="space-y-2">
                                  <p className="text-xs uppercase font-semibold text-neutral-500">CVV Response</p>
                                  <p className="text-white font-mono">{order.cvv_response || '—'}</p>
                                </div>
                                <div className="space-y-2">
                                  <p className="text-xs uppercase font-semibold text-neutral-500">Phone</p>
                                  <p className="text-white">{order.customer_phone || '—'}</p>
                                </div>
                                {order.billing_address && (
                                  <div className="space-y-2 md:col-span-3">
                                    <p className="text-xs uppercase font-semibold text-neutral-500">Billing Address</p>
                                    <p className="text-white text-sm">
                                      {order.billing_address.address}, {order.billing_address.city}, {order.billing_address.state} {order.billing_address.zip}, {order.billing_address.country}
                                    </p>
                                  </div>
                                )}
                                {order.cart_items && order.cart_items.length > 0 && (
                                  <div className="space-y-2 md:col-span-3">
                                    <p className="text-xs uppercase font-semibold text-neutral-500">Cart Items</p>
                                    <div className="flex flex-wrap gap-2">
                                      {order.cart_items.map((item, idx) => (
                                        <span key={idx} className="px-3 py-1 bg-neutral-700 rounded-lg text-sm text-white">
                                          {item.name} x{item.quantity} — ${(item.price * item.quantity).toFixed(2)}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminPage;
